import { useContext, type ChangeEvent } from 'react'
import { formatMoney } from 'accounting'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import useBillingTypes from '@hooks/useBillingTypes'
import useCurrentUser from '@hooks/useCurrentUser'
import useInvoiceItem, { useInvoiceItemForm } from '@hooks/useInvoiceItem'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import EntitySelectBox from '@components/EntitySelectBox'

import Actions from './Actions'

const MODAL_KEY = 'CreateOrEditInvoiceItemModal'

type ModalCallbacks = {
  closeModal: () => void,
  createInvoiceItem: (payload: HandleCallbackActionParams) => void,
  reprocessInvoiceItemToZuora: (payload: HandleCallbackActionParams) => void,
  updateInvoiceItem: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditInvoiceItemModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createInvoiceItem, updateInvoiceItem } = callbacks || {}

  const billingTypesPayload = useBillingTypes({
    performHttpRequests: true,
    filters: {
      charges_type: 'fee',
    },
  })

  const { isAdmin, isAgencyUser } = useCurrentUser()

  const { filteredBillingTypes, hasFilteredBillingTypes } = billingTypesPayload

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData[MODAL_KEY] || {}
  const initInvoiceItem = modalPayload?.invoiceItem || {}

  const newRecord = !initInvoiceItem.id

  const invoiceItemPayload = useInvoiceItem(initInvoiceItem)
  const {
    callbacks: {
      createInvoiceItem: createFn,
      updateInvoiceItem: updateFn,
    },
    creating,
    invoiceItem,
    updating,
  } = invoiceItemPayload

  const customRequiredFields = isAdmin ? [{ key: 'billing_type_id' }] : []

  const {
    entityState,
    entityState: {
      billing_type_id,
      description,
      price,
      quantity,
      service_id,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useInvoiceItemForm(invoiceItem, { customRequiredFields, validateOn: 'change' })

  const createOrUpdateInvoiceItemPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: { include_invoice_item_invoice_total: true },
    toastText: `Invoice Item ${newRecord ? 'Created' : 'Updated'}`,
  }

  const formattedTotal = formatMoney(quantity * price)

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine={
          invoiceItem?.id
            ? `Invoice Item: ${invoiceItem.id}`
            : 'Invoice Item'
        }
      />

      <SidebarModal.Content>
        <Form>
          {!isAgencyUser && (
            <Form.Field label="* Service">
              <EntitySelectBox
                entityKey="services"
                // @ts-expect-error TS2322
                onChange={(e: ChangeEvent<HTMLSelectElement>) => setEntityState({ service_id: e.target.value })}
                value={service_id}
              />
            </Form.Field>
          )}

          {isAdmin && hasFilteredBillingTypes && (
            <Form.Field label="* Billing Type" marginTop="large">
              {/*
               // @ts-expect-error TS2322 */}
              <select
                disabled={!newRecord && !!invoiceItem.billing_type_id}
                name="billing_type_id"
                value={billing_type_id}
                {...handlers}
              >
                <option value="">Please Select...</option>
                {filteredBillingTypes.map(billingType => (
                  <option key={billingType.id} value={billingType.id}>{billingType.title}</option>
                ))}
              </select>
            </Form.Field>
          )}

          {!!invoiceItem?.zuora_usage_id && (
            <Form.Field label="Zuora Usage ID" marginTop="large">
              <input disabled type="text" value={invoiceItem?.zuora_usage_id} />
            </Form.Field>
          )}

          <Form.Field label="* Description" errorMessage={errors.description} marginTop={!isAgencyUser ? 'large' : 0}>
            <input name="description" type="text" value={description} {...handlers} />
          </Form.Field>

          {!isAgencyUser && (
            <Form.Field label="* Quantity" errorMessage={errors.quantity} marginTop="large">
              <input name="quantity" type="text" value={quantity} {...handlers} />
            </Form.Field>
          )}

          <Form.Field label="* Price" errorMessage={errors.price} marginTop="large">
            <input name="price" type="number" value={price} {...handlers} />
          </Form.Field>

          {!isAgencyUser && (
            <Form.Field label="Amount" marginTop="large">
              <input name="amount" type="text" disabled value={formattedTotal} {...handlers} />
            </Form.Field>
          )}

          {invoiceItem.id && (
            <Actions callbacks={callbacks} invoiceItemPayload={invoiceItemPayload} isAdmin={isAdmin} />
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createInvoiceItem(createOrUpdateInvoiceItemPayload)
              : () => updateInvoiceItem(createOrUpdateInvoiceItemPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Invoice Item` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditInvoiceItemModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
